import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Users, Mail, Phone, Linkedin, FileText, Calendar,
  MapPin, Briefcase, Search, Filter, Download, Eye, Loader2,
  CheckCircle, Clock, Star, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const JobApplicantsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applicants, setApplicants] = useState([]);
  const [filteredApplicants, setFilteredApplicants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchApplicants();
  }, []);

  useEffect(() => {
    filterApplicants();
  }, [searchQuery, filterStatus, applicants]);

  const fetchApplicants = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/applicants`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApplicants(data.applicants || []);
        setFilteredApplicants(data.applicants || []);
      }
    } catch (error) {
      console.error('Error fetching applicants:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterApplicants = () => {
    let filtered = [...applicants];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(app =>
        app.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.job_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(app => app.status === filterStatus);
    }

    setFilteredApplicants(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'reviewed': return 'bg-yellow-100 text-yellow-700';
      case 'shortlisted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const stats = [
    {
      label: 'Total Applicants',
      value: applicants.length,
      color: 'from-blue-500 to-cyan-500',
      icon: Users
    },
    {
      label: 'New Applications',
      value: applicants.filter(a => a.status === 'new').length,
      color: 'from-green-500 to-emerald-500',
      icon: Clock
    },
    {
      label: 'Shortlisted',
      value: applicants.filter(a => a.status === 'shortlisted').length,
      color: 'from-purple-500 to-pink-500',
      icon: Star
    },
    {
      label: 'This Month',
      value: applicants.filter(a => {
        const appDate = new Date(a.applied_at);
        const now = new Date();
        return appDate.getMonth() === now.getMonth() && appDate.getFullYear() === now.getFullYear();
      }).length,
      color: 'from-orange-500 to-red-500',
      icon: Calendar
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-purple-50">
      <div className="container mx-auto px-6 py-8 pt-24">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-900">Job Applicants</h1>
              <p className="text-slate-600 mt-1">Track and manage all candidates who applied to your jobs</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-600 mb-1">{stat.label}</p>
                        <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                      </div>
                      <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Search and Filter */}
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, job title, or email..."
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={filterStatus === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterStatus('all')}
                  >
                    All
                  </Button>
                  <Button
                    variant={filterStatus === 'new' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterStatus('new')}
                  >
                    New
                  </Button>
                  <Button
                    variant={filterStatus === 'shortlisted' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterStatus('shortlisted')}
                  >
                    Shortlisted
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Applicants List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-violet-500 animate-spin mb-4" />
            <p className="text-slate-600">Loading applicants...</p>
          </div>
        ) : filteredApplicants.length === 0 ? (
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">
                {applicants.length === 0 ? 'No Applicants Yet' : 'No Results Found'}
              </h3>
              <p className="text-slate-500">
                {applicants.length === 0
                  ? 'Applicants who apply to your posted jobs will appear here'
                  : 'Try adjusting your search or filter criteria'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredApplicants.map((applicant, index) => (
              <Card key={index} className="bg-white border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    {/* Avatar */}
                    <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-2xl font-bold text-white">
                        {applicant.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-slate-900 mb-1">{applicant.name}</h3>
                          <p className="text-sm text-slate-600 mb-2">
                            Applied for: <strong>{applicant.job_title}</strong>
                          </p>
                        </div>
                        <Badge className={getStatusColor(applicant.status)}>
                          {applicant.status || 'New'}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        {applicant.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="w-4 h-4 text-slate-400" />
                            {applicant.email}
                          </div>
                        )}
                        {applicant.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-4 h-4 text-slate-400" />
                            {applicant.phone}
                          </div>
                        )}
                        {applicant.location && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            {applicant.location}
                          </div>
                        )}
                        {applicant.experience_years && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Briefcase className="w-4 h-4 text-slate-400" />
                            {applicant.experience_years}+ years experience
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge variant="secondary" className="text-xs">
                          <Calendar className="w-3 h-3 mr-1" />
                          Applied: {new Date(applicant.applied_at).toLocaleDateString()}
                        </Badge>
                        {applicant.source && (
                          <Badge variant="secondary" className="text-xs">
                            Source: {applicant.source}
                          </Badge>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {applicant.resume_url && (
                          <Button variant="outline" size="sm">
                            <FileText className="w-4 h-4 mr-2" />
                            Resume
                          </Button>
                        )}
                        {applicant.linkedin_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(applicant.linkedin_url, '_blank')}
                          >
                            <Linkedin className="w-4 h-4 mr-2" />
                            LinkedIn
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="ml-auto"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Full Profile
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobApplicantsPage;
